export type MatchReaction = "love" | "like" | "love_like" | "not_for_me" | null;

export type MatchApiRecord = {
  matchId: string;
  matchScore?: number | null;
  distanceMiles?: number | null;
  profilePicUrl?: string | null;
  coverImageUrl?: string | null;
  name?: string | null;
  topMatchTags?: string[] | null;
  isOnline?: boolean | null;
  profileSnapshot?: Record<string, unknown> | null;
  profileId?: string | null;
  matchCreatedAt?: string | null;
  reaction?: MatchReaction | null;
  counterpartReaction?: MatchReaction | null;
  isMutualLove?: boolean | null;
  isMatchMade?: boolean | null;
};

export type MatchApiResponse = {
  matches: MatchApiRecord[];
  page?: number;
  total?: number;
  hasNextPage?: boolean;
};

export type MatchFetchOptions = {
  previewRole?: "homeowner" | "designer" | null;
};

export type MatchProfileDetail = {
  profileId?: string | null;
  profilePicUrl?: string | null;
  heroImageUrl?: string | null;
  coverImageUrl?: string | null;
  location?: string | null;
  distanceMiles?: number | null;
  topMatchTags?: string[] | null;
  certifications?: string[] | null;
  education?: string[] | null;
  personalityStyleResult?: string | null;
  compatibilityRating?: number | null;
  isOnline?: boolean | null;
  isOffMarket?: boolean | null;
};

export type DesignerStatsResponse = {
  matches: number;
  projects: number;
};

export type MessagePermissionResponse = {
  allowed: boolean;
  reason?: string | null;
  homeownerReaction?: MatchReaction | null;
  designerReaction?: MatchReaction | null;
  senderReaction?: MatchReaction | null;
  recipientReaction?: MatchReaction | null;
};

const getApiUrl = () => {
  return (
    process.env.REACT_APP_API_URL ||
    (process.env.NODE_ENV === "development"
      ? "http://localhost:8080"
      : "https://hdg-api-service-485830218751.us-central1.run.app")
  );
};

export const fetchDesignerStats = async (
  designerId: string
): Promise<DesignerStatsResponse> => {
  const response = await fetch(
    `${getApiUrl()}/api/v1/designers/${encodeURIComponent(designerId)}/stats`,
    {
      credentials: "include",
    }
  );

  if (!response.ok) {
    throw new Error("Unable to load designer stats");
  }

  return (await response.json()) as DesignerStatsResponse;
};

export const fetchMatches = async (
  userId: string,
  page = 1,
  options?: MatchFetchOptions
): Promise<MatchApiResponse> => {
  const url = new URL(`${getApiUrl()}/api/v1/matches`);
  url.searchParams.set("userId", userId);
  url.searchParams.set("page", String(page));
  if (options?.previewRole) {
    url.searchParams.set("previewRole", options.previewRole);
  }

  const response = await fetch(url.toString(), {
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Unable to load matches");
  }

  const data = (await response.json()) as
    | MatchApiResponse
    | MatchApiRecord[]
    | { matches?: MatchApiRecord[]; page?: number; total?: number; hasNextPage?: boolean };

  if (Array.isArray(data)) {
    return { matches: data, page, total: data.length, hasNextPage: false };
  }

  return {
    matches: data.matches ?? [],
    page: data.page ?? page,
    total: data.total ?? data.matches?.length ?? 0,
    hasNextPage: data.hasNextPage ?? false,
  };
};

export const postMatchReaction = async (
  matchId: string,
  reaction: MatchReaction,
  userId?: string | null,
  profileId?: string | null
) => {
  const response = await fetch(
    `${getApiUrl()}/api/v1/matches/${encodeURIComponent(matchId)}/reaction`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ reaction, userId, profileId }),
    }
  );

  if (!response.ok) {
    throw new Error("Unable to update reaction");
  }

  return response.json();
};

export const fetchMatchReaction = async (
  userId: string,
  profileId: string
): Promise<MatchReaction> => {
  const url = new URL(`${getApiUrl()}/api/v1/matches/reaction`);
  url.searchParams.set("userId", userId);
  url.searchParams.set("profileId", profileId);

  const response = await fetch(url.toString(), {
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Unable to load match reaction");
  }

  const data = (await response.json()) as { reaction?: MatchReaction | null };
  return data.reaction ?? null;
};

export const fetchMessagePermission = async (
  senderId: string,
  senderRole: string,
  recipientId: string,
  recipientRole: string
): Promise<MessagePermissionResponse> => {
  const url = new URL(`${getApiUrl()}/api/v1/messages/permission`);
  url.searchParams.set("senderId", senderId);
  url.searchParams.set("senderRole", senderRole);
  url.searchParams.set("recipientId", recipientId);
  url.searchParams.set("recipientRole", recipientRole);

  const response = await fetch(url.toString(), {
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Unable to load messaging permission");
  }

  return (await response.json()) as MessagePermissionResponse;
};

export const fetchMatchProfile = async (
  profileId: string
): Promise<MatchProfileDetail> => {
  const response = await fetch(
    `${getApiUrl()}/api/v1/profiles/${encodeURIComponent(profileId)}`,
    {
      credentials: "include",
    }
  );

  if (!response.ok) {
    throw new Error("Unable to load profile detail");
  }

  return (await response.json()) as MatchProfileDetail;
};

export const fetchPresenceStatuses = async (
  ids: string[],
  role: string
): Promise<Record<string, boolean>> => {
  if (!ids.length || !role) return {};
  const url = new URL(`${getApiUrl()}/api/v1/presence`);
  url.searchParams.set("role", role);
  url.searchParams.set("ids", ids.join(","));

  const response = await fetch(url.toString(), {
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Unable to load presence");
  }

  const data = (await response.json()) as { presence?: Record<string, boolean> };
  return data.presence ?? {};
};
